import React, { useEffect, useRef, useState } from "react";
import { sendBioMessage } from "@/services/bioLeadService";
import { replyOf } from "@/components/BioMessageSheet";
import "./ShowcaseFitForm.scss";

const firstNameOf = (name) => String(name || "").trim().split(" ")[0] || "the agent";

export const fitMessageOf = () => "I own a property that may fit your buyer.";

const ShowcaseFitForm = ({ agent, wantAdId, title, showcaseKey, autoOpen = false, onOpen }) => {
  const [open, setOpen] = useState(autoOpen);
  const [values, setValues] = useState(() => ({ name: "", email: "", message: fitMessageOf(), website: "" }));
  const [errors, setErrors] = useState({});
  const [problem, setProblem] = useState("");
  const [state, setState] = useState("compose");
  const [sentTo, setSentTo] = useState("");
  const openedAt = useRef(Date.now());
  const name = firstNameOf(agent?.name);

  const update = (key) => (event) => {
    setValues((current) => ({ ...current, [key]: event.target.value }));
    setErrors((current) => ({ ...current, [key]: undefined }));
  };

  const start = () => {
    openedAt.current = Date.now();
    setOpen(true);
    if (onOpen) onOpen();
  };

  useEffect(() => {
    if (autoOpen) setOpen(true);
  }, [autoOpen]);

  const submit = async (event) => {
    event.preventDefault();
    setState("sending");
    setProblem("");
    try {
      const response = await sendBioMessage(agent.slug, {
        ...values,
        elapsedMs: Date.now() - openedAt.current,
        source: "showcase",
        showcaseKey,
        wantAdId,
      });
      setSentTo(response?.result?.email || values.email.trim());
      setState("sent");
    } catch (error) {
      const reply = replyOf(error);
      setErrors(reply.errors);
      setProblem(Object.keys(reply.errors).length ? "" : reply.message);
      setState("compose");
    }
  };

  if (!open) {
    return (
      <button type="button" className="showcase-fit__start" onClick={start}>
        Contact&nbsp;{name}
      </button>
    );
  }

  if (state === "sent") {
    return (
      <div className="showcase-fit showcase-fit--sent" role="status">
        <h3>Check your email</h3>
        <p>
          We sent a link to <strong>{sentTo}</strong>. Tap it and your message goes to {name}.
        </p>
        <p className="showcase-fit__hint">The link works for 24&nbsp;hours.</p>
        <button type="button" className="showcase-fit__link" onClick={() => setState("compose")}>
          Wrong email? Change it
        </button>
      </div>
    );
  }

  return (
    <form className="showcase-fit" onSubmit={submit} noValidate aria-labelledby="showcase-fit-title">
      <h3 id="showcase-fit-title">Tell {name} about your&nbsp;home</h3>
      <p className="showcase-fit__hint">
        {name} gets your message after you confirm your email. Your email is only shared with {name}.
      </p>
      <label htmlFor="showcase-fit-name">Your name</label>
      <input id="showcase-fit-name" autoComplete="name" value={values.name} onChange={update("name")} aria-invalid={Boolean(errors.name)} />
      {errors.name ? <p className="showcase-fit__error">{errors.name}</p> : null}
      <label htmlFor="showcase-fit-email">Email</label>
      <input
        id="showcase-fit-email"
        type="email"
        autoComplete="email"
        value={values.email}
        onChange={update("email")}
        aria-invalid={Boolean(errors.email)}
      />
      {errors.email ? <p className="showcase-fit__error">{errors.email}</p> : null}
      <label htmlFor="showcase-fit-message">About your home</label>
      <textarea
        id="showcase-fit-message"
        rows={4}
        value={values.message}
        onChange={update("message")}
        aria-invalid={Boolean(errors.message)}
      />
      {errors.message ? <p className="showcase-fit__error">{errors.message}</p> : null}
      <input
        className="showcase-fit__trap"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        name="website"
        value={values.website}
        onChange={update("website")}
      />
      {problem ? (
        <p className="showcase-fit__error" role="alert">
          {problem}
        </p>
      ) : null}
      <button type="submit" className="showcase-fit__start" disabled={state === "sending"}>
        {state === "sending" ? "Sending…" : `Send to ${name}`}
      </button>
    </form>
  );
};

export default ShowcaseFitForm;
